import { useEffect, useRef } from "react";
import type { AuthContextValue } from "@/contexts/AuthContext";
import { useAnchorEntitlements } from "@/contexts/AnchorEntitlementsContext";
import { useWorkspaceOrg } from "@/contexts/WorkspaceOrgContext";
import { canAccessPrimaryMode, type PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { canAccessByAnchor } from "@/lib/entitlements/modeAnchorRequirement";
import { queueBuilderAssistantSession } from "@/lib/manufacturing/builderAssistantOpenSession";

function pendingQcReport(): { sessionId: string; org: string } | null {
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get("qcReport")?.trim() ?? "";
  if (!sessionId) return null;
  return { sessionId, org: params.get("org")?.trim() ?? "" };
}

function clearQcReportParams() {
  const params = new URLSearchParams(window.location.search);
  params.delete("qcReport");
  params.delete("org");
  const next = params.toString();
  window.history.replaceState({}, "", next ? `${window.location.pathname}?${next}` : window.location.pathname);
}

/** Opens a QC report link in the organization the link names. */
export function useQcReportLink(
  auth: AuthContextValue | null,
  setMode: (mode: PrimaryMode) => void,
) {
  const { workspaceOrganizationId, setWorkspaceOrganizationId } = useWorkspaceOrg();
  const { anchors, loading, scopedOrganizationId } = useAnchorEntitlements();
  const triedOrg = useRef<string | null>(null);

  useEffect(() => {
    const pending = pendingQcReport();
    if (!pending || !auth || auth.status !== "ready") return;
    if (pending.org && workspaceOrganizationId !== pending.org) {
      if (triedOrg.current !== pending.org) {
        triedOrg.current = pending.org;
        setWorkspaceOrganizationId(pending.org);
      }
      return;
    }
    if (loading || (pending.org && scopedOrganizationId !== pending.org)) return;
    if (!canAccessPrimaryMode("mfgBuilderAssistant", auth)) return;
    if (!canAccessByAnchor("mfgBuilderAssistant", anchors)) return;
    setMode("mfgBuilderAssistant");
    queueBuilderAssistantSession(pending.sessionId, null, true);
    clearQcReportParams();
  }, [
    auth,
    anchors,
    loading,
    scopedOrganizationId,
    setMode,
    setWorkspaceOrganizationId,
    workspaceOrganizationId,
  ]);
}

export function qcReportLinkPending(): boolean {
  if (typeof window === "undefined") return false;
  return Boolean(new URLSearchParams(window.location.search).get("qcReport"));
}
